/**
 * Shared, Angular-free pieces of the Body Composition overview (#5456,
 * design-docs/coaching-dashboard.md).
 *
 * Every trend here is a Theil-Sen fit through the app's own estimators in `util/statistics.ts`.
 * Nothing in this file decides anything about a plan: it only describes readings for display.
 * Values stay in kg, cm and mm; converting to the user's units happens where they are shown.
 */

import { DateTime } from 'luxon';
import { pickOneEntryPerSingletonSlot } from './data-model/log-entry-user-data';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType, Log_Entry } from './data-model/user-log';
import { interceptEstimationPercentile, slopeEstimationPercentile } from './util/statistics';
import { hasEnoughWeighInsForWeightTrend, MIN_BODY_FAT_SAMPLES_FOR_TREND } from './weight-trend';

/**
 * The three answers the app gives to "is this person's progress on track?".
 *
 * Declared here, in `shared/`, because the weekly check-up document stores one of these values
 * and the shared data model cannot import from the client. The function that works the value
 * out from a plan check result is `progressStatus` in `client/src/services/progress-status.ts`.
 */
export enum ProgressStatus {
  /** no change is needed and the plan check had enough to say so */
  OnTrack = 'OnTrack',
  /** the plan check wants the plan adjusted */
  OffTrack = 'OffTrack',
  /** the plan check could not judge progress (too few readings, a recent change, ...) */
  Undetermined = 'Undetermined',
}

/** Which way the body-fat series is heading, read from its 30th and 70th percentile slopes. */
export enum FatTrendDirection {
  /** even the steeper-upward slope is below zero */
  Down = 'Down',
  /** even the steeper-downward slope is above zero */
  Up = 'Up',
  /** the two slopes straddle zero, so the readings do not say */
  Unclear = 'Unclear',
  /** there are no slopes: too few readings */
  NotEnough = 'NotEnough',
}

/** The tunnel's two percentiles and the line's own, used by every fit in this file. */
export const TUNNEL_LOW_PERCENTILE = 0.3;
export const TUNNEL_HIGH_PERCENTILE = 0.7;
const MEDIAN_PERCENTILE = 0.5;

/** The running trend fits a window this long ending on each day. */
export const RUNNING_WINDOW_DAYS_WEIGHT = 14;
export const RUNNING_WINDOW_DAYS_FAT = 28;

/** One reading. `x` is a whole day number on any fixed scale (the caller picks day 0); `y` the value. */
export interface TrendPoint {
  x: number;
  y: number;
}

/** The trend at one day: the median line and the two tunnel lines. `low` is never above `high`. */
export interface TrendBandRow {
  x: number;
  low: number;
  mid: number;
  high: number;
}

/** One straight fit over a period, given as its two ends. */
export interface StraightTrend {
  start: TrendBandRow;
  end: TrendBandRow;
  /** the median slope, per day */
  slopePerDay: number;
}

/**
 * Weight change per week as a percentage of body weight.
 * Example: a slope of -0.42 kg per week at 82.4 kg is -0.51 (percent).
 * Undefined when either number is missing or the body weight is not positive.
 */
export function weightChangePercentPerWeek(weightSlope_kgwk: number | undefined, estimatedBW_kg: number | undefined): number | undefined {
  if (!isFiniteNumber(weightSlope_kgwk) || !isFiniteNumber(estimatedBW_kg) || estimatedBW_kg <= 0) {
    return undefined;
  }
  return weightSlope_kgwk / estimatedBW_kg * 100;
}

/** The direction of the fat trend from its 30th (`bottom`) and 70th (`top`) percentile slopes. */
export function fatTrendDirection(bottom: number | undefined, top: number | undefined): FatTrendDirection {
  if (!isFiniteNumber(bottom) || !isFiniteNumber(top)) {
    return FatTrendDirection.NotEnough;
  }
  if (top < 0) {
    return FatTrendDirection.Down;
  }
  if (bottom > 0) {
    return FatTrendDirection.Up;
  }
  return FatTrendDirection.Unclear;
}

/** a whole day number for an ISO date; consecutive calendar days are 1 apart */
export function dayNumber(dateIso: string): number {
  return Math.round(DateTime.fromISO(dateIso, { zone: 'utc' }).toMillis() / 86_400_000);
}

/** the ISO date of a day number */
export function dayIso(day: number): string {
  return DateTime.fromMillis(day * 86_400_000, { zone: 'utc' }).toISODate();
}

/**
 * Keep one reading per day: the LAST one given for each day wins, as the Progress page does.
 * Readings with a non-finite day or value are dropped. The answer is sorted by day.
 */
export function onePointPerDay(points: readonly TrendPoint[]): TrendPoint[] {
  const byDay = new Map<number, TrendPoint>();
  for (const point of points) {
    if (!point || !isFiniteNumber(point.x) || !isFiniteNumber(point.y)) continue;
    byDay.set(Math.round(point.x), { x: Math.round(point.x), y: point.y });
  }
  return [...byDay.values()].sort((a, b) => a.x - b.x);
}

/** which kind of series a trend is fitted through; each has its own minimum-data rule */
export enum TrendSeries {
  /** body weight: weigh-ins, usually daily */
  Weight = 'Weight',
  /** a body-fat series: the skinfold mean, one skinfold site, or the waist */
  BodyFat = 'BodyFat',
}

/**
 * Whether these readings are enough for a trend, by the same minimum-data rule the plan check
 * uses: for weight, 5 weigh-ins in the 7 days up to `lastDay` or 6 in the window; for a fat
 * series, 3 readings.
 */
export function hasEnoughReadingsForTrend(points: readonly TrendPoint[], lastDay: number, series: TrendSeries): boolean {
  if (series === TrendSeries.BodyFat) {
    return points.length >= MIN_BODY_FAT_SAMPLES_FOR_TREND;
  }
  // the weight rule counts days back from the end of the window, 0 being the last day
  return hasEnoughWeighInsForWeightTrend(points.map(p => ({ x: p.x - lastDay })));
}

/** The three lines of one fit: slope and intercept at the 30th, 50th and 70th percentile slope. */
interface BandFit {
  low: { slope: number; intercept: number };
  mid: { slope: number; intercept: number };
  high: { slope: number; intercept: number };
}

/** Fit the median line and the two tunnel lines. Each line takes its own median intercept. */
function fitBand(points: readonly TrendPoint[]): BandFit | undefined {
  const mutable = points as TrendPoint[];
  const line = (percentile: number) => {
    const slope = slopeEstimationPercentile(mutable, percentile);
    if (!isFiniteNumber(slope)) return undefined;
    const intercept = interceptEstimationPercentile(mutable, slope, MEDIAN_PERCENTILE);
    return isFiniteNumber(intercept) ? { slope, intercept } : undefined;
  };
  const low = line(TUNNEL_LOW_PERCENTILE);
  const mid = line(MEDIAN_PERCENTILE);
  const high = line(TUNNEL_HIGH_PERCENTILE);
  // if all readings share one day there are no slopes at all
  if (!low || !mid || !high) return undefined;
  return { low, mid, high };
}

/** Read a fit at one day. The two tunnel lines cross inside the window, so order them here. */
function readBand(fit: BandFit, x: number): TrendBandRow {
  const a = fit.low.slope * x + fit.low.intercept;
  const b = fit.high.slope * x + fit.high.intercept;
  return { x, low: Math.min(a, b), mid: fit.mid.slope * x + fit.mid.intercept, high: Math.max(a, b) };
}

/**
 * One fit over the given readings, drawn from `firstDay` to `lastDay`.
 * Undefined when the readings fail the minimum-data rule (see `hasEnoughReadingsForTrend`).
 */
export function straightTrend(
  points: readonly TrendPoint[],
  options: {
    firstDay: number;
    lastDay: number;
    series: TrendSeries;
    /**
     * true when the readings are the ones the plan check itself fitted and it produced a slope.
     * The check already applied the minimum-data rule, counted in its own day offsets; applying
     * it again here in calendar days could disagree at the edges and leave the verdict's chart
     * without the line the verdict was read from.
     */
    alreadyJudgedEnough?: boolean;
  },
): StraightTrend | undefined {
  const daily = onePointPerDay(points);
  if (!options.alreadyJudgedEnough && !hasEnoughReadingsForTrend(daily, options.lastDay, options.series)) {
    return undefined;
  }
  const fit = fitBand(daily);
  if (!fit) return undefined;
  return {
    start: readBand(fit, options.firstDay),
    end: readBand(fit, options.lastDay),
    slopePerDay: fit.mid.slope,
  };
}

/**
 * The running trend for long ranges: for each day from `firstDay` to `lastDay`, the trend at that
 * day, or `undefined` where there is too little data (the chart leaves a gap there).
 *
 * THIS IS THE ONE FUNCTION cybsol/mh-physique#6463 will replace the inside of. Its signature is
 * the contract; callers must not depend on how the rows are worked out.
 *
 * Interim method: fit every window of `windowDays` ending on each day of the range (and on the
 * days just after it that still cover days inside it, up to `lastDay`). A window counts only if
 * it passes the minimum-data rule. A day's `mid` is the mean of the median lines of all counted
 * windows that cover that day, read at that day; `low` and `high` are the means of their two
 * tunnel lines. Averaging the overlapping fits is what makes the curve smooth instead of jumping
 * whenever a reading enters or leaves a single window.
 */
export function runningTrend(
  points: readonly TrendPoint[],
  options: { firstDay: number; lastDay: number; windowDays: number; series: TrendSeries },
): (TrendBandRow | undefined)[] {
  const { firstDay, lastDay, windowDays, series } = options;
  const daily = onePointPerDay(points);
  const rows: (TrendBandRow | undefined)[] = [];
  if (!(lastDay >= firstDay) || !(windowDays >= 1)) {
    return rows;
  }

  // for each window end day, fit the readings of the window ending there (if they are enough)
  const fitByEndDay = new Map<number, BandFit>();
  let from = 0; // index of the first reading that can still be inside a later window
  for (let endDay = firstDay; endDay <= lastDay; endDay++) {
    while (from < daily.length && daily[from].x <= endDay - windowDays) from++;
    const windowPoints: TrendPoint[] = [];
    for (let i = from; i < daily.length && daily[i].x <= endDay; i++) {
      windowPoints.push(daily[i]);
    }
    if (!hasEnoughReadingsForTrend(windowPoints, endDay, series)) continue;
    const fit = fitBand(windowPoints);
    if (fit) fitByEndDay.set(endDay, fit);
  }

  // for each day, average every counted window that covers it
  for (let day = firstDay; day <= lastDay; day++) {
    let low = 0, mid = 0, high = 0, count = 0;
    const lastEnd = Math.min(lastDay, day + windowDays - 1);
    for (let endDay = day; endDay <= lastEnd; endDay++) {
      const fit = fitByEndDay.get(endDay);
      if (!fit) continue;
      const row = readBand(fit, day);
      low += row.low; mid += row.mid; high += row.high; count++;
    }
    rows.push(count > 0 ? { x: day, low: low / count, mid: mid / count, high: high / count } : undefined);
  }
  return rows;
}

/**
 * Theil-Sen change per week over the given readings: the median slope times 7.
 * Undefined with fewer than 2 readings on different days.
 */
export function theilSenChangePerWeek(points: readonly TrendPoint[]): number | undefined {
  const daily = onePointPerDay(points);
  if (daily.length < 2) return undefined;
  const slope = slopeEstimationPercentile(daily, MEDIAN_PERCENTILE);
  return isFiniteNumber(slope) ? slope * 7 : undefined;
}

/** One day of the check-up week for the intake cards. */
export interface KcalAdherenceDay {
  /** kcal logged that day */
  consumed: number;
  /** that day's kcal target */
  target: number;
  /** whether the user left the day ticked as fully logged */
  ticked: boolean;
}

export interface KcalAdherence {
  /** mean kcal logged over the ticked days */
  meanConsumed: number;
  /** mean kcal target over the ticked days; undefined when no ticked day has a positive target */
  meanTarget: number | undefined;
  /** meanConsumed as a percentage of meanTarget; undefined when there is no target */
  percent: number | undefined;
  /** how many days were ticked */
  tickedDays: number;
  /** how many days have a day entry at all (can be under 7) */
  totalDays: number;
}

/** Intake against target over the ticked days. Undefined when no day is ticked. */
export function kcalAdherence(days: readonly KcalAdherenceDay[]): KcalAdherence | undefined {
  const usable = (days || []).filter(d => !!d && isFiniteNumber(d.consumed));
  const ticked = usable.filter(d => d.ticked);
  if (ticked.length === 0) return undefined;
  const meanConsumed = ticked.reduce((sum, d) => sum + d.consumed, 0) / ticked.length;
  const withTarget = ticked.filter(d => isFiniteNumber(d.target) && d.target > 0);
  const meanTarget = withTarget.length > 0 ? withTarget.reduce((sum, d) => sum + d.target, 0) / withTarget.length : undefined;
  return {
    meanConsumed,
    meanTarget,
    percent: meanTarget !== undefined ? meanConsumed / meanTarget * 100 : undefined,
    tickedDays: ticked.length,
    totalDays: usable.length,
  };
}

export interface TrainingAdherence {
  /** days of the week, before today, that have a workout entry */
  plannedDays: number;
  /** those of them whose workout is Done */
  doneDays: number;
}

/** the parts of a workout entry the count reads */
export type TrainingAdherenceEntry = Pick<Log_Entry, '_id' | 'type' | 'time_utc' | 'timeOffset_min' | 'state'>;

/**
 * Training days planned against done, for the given week.
 *
 * Planned = days of `weekDayIsos` strictly before `todayIso` that have a workout entry.
 * Done = those whose workout's state is `Done`. Every other state (`Scheduled`, `Planned`,
 * `Incomplete`, `Skipped`) is planned and not done; a missed past workout is normally still
 * `Scheduled`. Several workout documents can sit on one day, so each day is first reduced to one
 * with `pickOneEntryPerSingletonSlot`, the rule the day list itself uses.
 *
 * Undefined when the week has no workout entry before today (the card then says so).
 *
 * @param referenceTime any time in the zone the entries' wall-clock times should be read in
 */
export function trainingAdherence(
  workoutEntries: readonly TrainingAdherenceEntry[],
  weekDayIsos: readonly string[],
  todayIso: string,
  referenceTime: DateTime,
): TrainingAdherence | undefined {
  const countedDays = new Set(weekDayIsos.filter(day => day < todayIso));
  const entriesByDay = new Map<string, TrainingAdherenceEntry[]>();
  for (const entry of workoutEntries || []) {
    if (!entry || entry.type !== LogRecordType.Workout || !entry.time_utc) continue;
    const entryDayIso = getEntryLocalTimeInDesiredTimeZone(entry, referenceTime).toISODate();
    if (!countedDays.has(entryDayIso)) continue;
    const list = entriesByDay.get(entryDayIso);
    if (list) list.push(entry); else entriesByDay.set(entryDayIso, [entry]);
  }
  if (entriesByDay.size === 0) return undefined;

  let doneDays = 0;
  for (const dayEntries of entriesByDay.values()) {
    // an entry here holds only the fields above, so "holds user data" is answered from its state:
    // a Done or Incomplete workout is one the user really trained
    const { kept } = pickOneEntryPerSingletonSlot(dayEntries as TrainingAdherenceEntry[], referenceTime,
      entry => entry.state === LogRecordState.Done || entry.state === LogRecordState.Incomplete);
    if (kept.some(entry => entry.state === LogRecordState.Done)) doneDays++;
  }
  return { plannedDays: entriesByDay.size, doneDays };
}

/** true for a real number: not NaN, not infinite, and not a value of another type from a damaged document */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
